/**
 * The API's error shape: RFC 7807 `application/problem+json`.
 *
 * The backend guarantees a stable, machine-readable `code` alongside prose that is translated per
 * request. The dashboard branches on `code` and shows either its own translation of that code or,
 * failing that, the server's already-localised `detail` — never a raw key and never an English
 * string to an Arabic reader.
 */

export interface ProblemDocument {
  readonly type?: string;
  readonly title?: string;
  readonly status?: number;
  readonly detail?: string;
  readonly code?: string;
  readonly requestId?: string;
  readonly timestamp?: string;
  readonly errors?: Record<string, string>;
  readonly [key: string]: unknown;
}

/** An error the API reported deliberately. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  readonly fieldErrors: Record<string, string>;
  readonly problem: ProblemDocument;

  constructor(status: number, problem: ProblemDocument) {
    super(problem.detail ?? problem.title ?? `Request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = problem.code ?? 'unexpected';
    this.requestId = problem.requestId;
    this.fieldErrors = problem.errors ?? {};
    this.problem = problem;
  }

  /** True when the session is gone and the caller should return to sign-in. */
  get isAuthenticationFailure(): boolean {
    return this.code === 'authentication_required' || this.code === 'invalid_refresh_token';
  }

  get isValidationFailure(): boolean {
    return this.code === 'validation_failed';
  }
}

/** The API could not be reached at all: offline, DNS, TLS, or the server is down. */
export class NetworkError extends Error {
  constructor(cause?: unknown) {
    super('Could not reach the Laseera API');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export function isProblemDocument(value: unknown): value is ProblemDocument {
  return typeof value === 'object' && value !== null;
}

/**
 * Chooses the message to show for an error.
 *
 * Prefers the dashboard's own translation of the business rule that refused the request, then of
 * the error code, because either can be phrased for the screen the user is on. A business-rule
 * refusal's `detail` is the server's generic "a clinic rule prevents this", so the rule is what
 * says why (CR-21). Falls back to the server's `detail`, which is already in the reader's language,
 * and only then to a generic message.
 */
export function messageForError(
  error: unknown,
  translate: (key: string, options?: { defaultValue?: string }) => string,
): string {
  if (error instanceof ApiError) {
    const rule = error.problem.rule;
    if (typeof rule === 'string' && rule) {
      const byRule = translate(`errors.rules.${rule}`, { defaultValue: '' });
      if (byRule) {
        return byRule;
      }
    }
    const translated = translate(`errors.${error.code}`, { defaultValue: '' });
    if (translated) {
      return translated;
    }
    return error.problem.detail ?? translate('errors.unexpected');
  }
  if (error instanceof NetworkError) {
    return translate('errors.network');
  }
  return translate('errors.unexpected');
}
